import { Player } from "@lottiefiles/react-lottie-player";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import {
  buildCallbackInput,
  hasSelectableOption,
} from "../../../../utils/customization/configuration";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import { isMobileSize } from "../../../../utils/customization/device";
import Tooltip from "../../../atoms/Tooltip";
import ConfirmModal from "../../ConfirmModal";
import PagesSummary from "../PagesSummary";
import ProductOptions from "../ProductOptions";
import animationSettings from "./settings-icon.json";
import "./style.scss";

const ANIMATION_SIZE = 28;
const TOOLTIP_MARGIN = 8;

interface Props {
  showOptions?: boolean;
  showProductName?: boolean;
}

const CharacteristicsSummary: FunctionComponent<Props> = ({
  showOptions = true,
  showProductName = true,
}) => {
  const { configuration, content, dispatch, template } = useCustomizationContext();
  const intl = useIntl();

  const characteristics = configuration?.characteristics;
  const product = (characteristics && characteristics[0]) || null;

  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const callbackInput = buildCallbackInput(content, noCoverPageDefinitions);

  let priceSummaryElement = null;
  const SummaryComponent = configuration && configuration.summaryComponent;
  if (SummaryComponent) {
    priceSummaryElement = <SummaryComponent input={callbackInput} />;
  }

  const hasOptionsMenu = hasSelectableOption(configuration, template);

  const [displayOptionsModal, setDisplayOptionsModal] = useState<boolean>(false);
  const [displayTooltip, setDisplayTooltip] = useState<boolean>(false);
  const characteristicsItems = characteristics?.map((characteristic, index) => {
    if (index === 0 && showProductName === true) {
      return null;
    }
    const startIndex = showProductName === true ? 1 : 0;
    const separator =
      index > startIndex ? <span className="CharacteristicsSummary-list-separator">|</span> : null;
    return (
      <li className="CharacteristicsSummary-list-item" key={characteristic}>
        {separator}
        {characteristic}
      </li>
    );
  });
  const settingsRef = useRef<HTMLLIElement>(null);
  const lottieRef = useRef<Player>(null);

  const handleOnMouseEnter = () => {
    if (lottieRef.current) {
      lottieRef.current.setLoop(true);
      lottieRef.current.play();
    }
  };
  const handleOnMouseLeave = () => {
    if (lottieRef.current) {
      lottieRef.current.setLoop(false);
      lottieRef.current.stop();
    }
  };
  const handleOnMouseEnterList = () => {
    setDisplayTooltip(true);
  };
  const handleOnMouseLeaveList = () => {
    setDisplayTooltip(false);
  };
  const handleOnClickSettings = () => {
    setDisplayOptionsModal(true);
  };
  const handleDismissModale = () => {
    setDisplayOptionsModal(false);
    dispatch({ type: "setEventQueue", way: "use" });
  };
  const showHideTooltip = () => {
    setDisplayTooltip(true);
    window.setTimeout(handleOnMouseLeaveList, 3300); // 3s + 300ms for animation time
  };

  useEffect(() => {
    if (hasOptionsMenu && isMobileSize() === false) {
      const onceDisplayed = localStorage.getItem("cheerz_options_modale");
      if (!onceDisplayed) {
        dispatch({ type: "setEventQueue", way: "add", event: handleOnClickSettings });
        localStorage.setItem("cheerz_options_modale", "1");
      }
      dispatch({ type: "setEventQueue", way: "add", event: showHideTooltip });
    }
  }, []);

  return (
    <div className="CharacteristicsSummary">
      <div className="CharacteristicsSummary-content">
        {showProductName && (product || priceSummaryElement) && (
          <h2 className="CharacteristicsSummary-title">
            {product} {priceSummaryElement}
          </h2>
        )}

        <ul
          className="CharacteristicsSummary-list"
          onClick={handleOnClickSettings}
          onMouseEnter={handleOnMouseEnterList}
          onMouseLeave={handleOnMouseLeaveList}
        >
          <li className="CharacteristicsSummary-list-summary">
            <PagesSummary />{" "}
            {showProductName === false && (
              <>
                <span>&nbsp;·&nbsp;</span>
                {priceSummaryElement}
              </>
            )}
          </li>
          {characteristicsItems}
          {hasOptionsMenu && showOptions === false && (
            <li
              className="CharacteristicsSummary-list-item CharacteristicsSummary-list-button"
              ref={settingsRef}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            >
              <Player
                autoplay={true}
                loop={false}
                renderer="svg"
                ref={lottieRef}
                src={animationSettings}
                style={{
                  height: `${ANIMATION_SIZE}px`,
                  width: `${ANIMATION_SIZE}px`,
                }}
              ></Player>
              <Tooltip
                display={displayTooltip}
                distance={TOOLTIP_MARGIN}
                placement={settingsRef.current?.getBoundingClientRect()}
              >
                {intl.formatMessage({ id: "kustom_CharacteristicsSummary_settings_tooltip" })}
              </Tooltip>
            </li>
          )}
        </ul>
      </div>

      {showOptions && hasOptionsMenu && (
        <div className="CharacteristicsSummary-buttons">
          <ProductOptions
            configuration={configuration}
            locale={content.locale}
            template={template}
          />
        </div>
      )}
      {showOptions === false && hasOptionsMenu && (
        <ConfirmModal
          display={displayOptionsModal}
          showDismissButton={false}
          onDismiss={handleDismissModale}
          confirmLabel={intl.formatMessage({ id: "kustom_EditableBookTitle_validate" })}
        >
          <div className="CharacteristicsSummary-OptionsModal">
            <div className="CharacteristicsSummary-OptionsModal-head">
              <h2 className="CharacteristicsSummary-OptionsModal-head-title">
                {intl.formatMessage({ id: "kustom_CharacteristicsSummary_optionsModal_title" })}
              </h2>
              <div className="CharacteristicsSummary-OptionsModal-head-summary">
                {product}
                <span>&nbsp;·&nbsp;</span>
                {priceSummaryElement}
              </div>
            </div>
            <ProductOptions
              configuration={configuration}
              locale={content.locale}
              template={template}
            />
          </div>
        </ConfirmModal>
      )}
    </div>
  );
};

export default CharacteristicsSummary;
